import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'urbanik_popup_dismissed';

// Frequency gates: returns true if we should show the popup right now.
const shouldShow = (popup) => {
  if (!popup || popup.enabled === false) return false;
  // Date window
  const now = Date.now();
  if (popup.start_date) {
    const start = new Date(popup.start_date).getTime();
    if (!Number.isNaN(start) && now < start) return false;
  }
  if (popup.end_date) {
    const end = new Date(popup.end_date).getTime();
    if (!Number.isNaN(end) && now > end) return false;
  }
  // Frequency
  const freq = popup.frequency || 'session';
  if (freq === 'always') return true;
  if (freq === 'session') {
    return !sessionStorage.getItem(STORAGE_KEY);
  }
  if (freq === 'daily') {
    const last = localStorage.getItem(STORAGE_KEY);
    if (!last) return true;
    const lastDay = new Date(parseInt(last, 10)).toDateString();
    const today = new Date().toDateString();
    return lastDay !== today;
  }
  return true;
};

const markDismissed = (frequency) => {
  const ts = Date.now().toString();
  if (frequency === 'daily') {
    localStorage.setItem(STORAGE_KEY, ts);
  } else if (frequency === 'session') {
    sessionStorage.setItem(STORAGE_KEY, ts);
  }
  // 'always' → never mark
};

// Sanitize button hrefs to avoid javascript:/data: URI injection
const safeHref = (href) => {
  if (!href || typeof href !== 'string') return null;
  const trimmed = href.trim();
  if (/^(https?:|mailto:|tel:|\/|#)/i.test(trimmed)) return trimmed;
  return null;
};

export default function WelcomePopup() {
  const [open, setOpen] = useState(false);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        const cfg = res.data?.popup;
        if (cancelled || !cfg) return;
        setPopup(cfg);
        if (shouldShow(cfg)) {
          // small delay so the page starts rendering first
          setTimeout(() => !cancelled && setOpen(true), 800);
        }
      } catch (e) {
        // Silent fail — popup is non-critical
        console.warn('Welcome popup config unavailable:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (popup) markDismissed(popup.frequency || 'session');
  }, [popup]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, handleClose]);

  if (!open || !popup) return null;

  const primaryHref = safeHref(popup.primary_link);
  const secondaryHref = safeHref(popup.secondary_link);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-popup-title"
      data-testid="welcome-popup"
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={handleClose}
        className="absolute inset-0 bg-[#0A1628]/70 backdrop-blur-sm transition-opacity"
        data-testid="welcome-popup-overlay"
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-lg sm:max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Image */}
        {popup.image_url && (
          <div className="relative w-full aspect-[16/9] bg-[#1E3A5F]">
            <img
              src={popup.image_url}
              alt={popup.title || 'URBANIK 2026'}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Close */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Fermer la popup"
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[#1E3A5F] flex items-center justify-center shadow-md transition-all"
          data-testid="welcome-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="px-6 sm:px-8 py-7 sm:py-8">
          {popup.title && (
            <h2 id="welcome-popup-title" className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] tracking-tight">
              {popup.title}
            </h2>
          )}
          {popup.subtitle && (
            <p className="mt-2 text-sm sm:text-base text-[#F5A623] font-semibold">
              {popup.subtitle}
            </p>
          )}
          {popup.description && (
            <p className="mt-4 text-sm sm:text-base text-[#4A5568] leading-relaxed">
              {popup.description}
            </p>
          )}

          {(popup.primary_label || popup.secondary_label) && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {popup.primary_label && primaryHref && (
                <a
                  href={primaryHref}
                  target={primaryHref.startsWith('http') ? '_blank' : undefined}
                  rel={primaryHref.startsWith('http') ? 'noopener noreferrer' : undefined}
                  onClick={handleClose}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#F5A623] hover:bg-[#E09615] text-[#1E3A5F] text-sm font-semibold transition-all shadow-md"
                  data-testid="welcome-popup-primary"
                >
                  {popup.primary_label}
                  <ArrowRight className="w-4 h-4" />
                </a>
              )}
              {popup.secondary_label && secondaryHref && (
                <a
                  href={secondaryHref}
                  target={secondaryHref.startsWith('http') ? '_blank' : undefined}
                  rel={secondaryHref.startsWith('http') ? 'noopener noreferrer' : undefined}
                  onClick={handleClose}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#E1E8F0] hover:bg-[#F7F9FC] text-[#1E3A5F] text-sm font-semibold transition-all"
                  data-testid="welcome-popup-secondary"
                >
                  {popup.secondary_label}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
